<template>
  <Dashboard :title="$t('viewer.page.title')" iconsOnly="true" noPadding="true">
    <!-- Loading State -->
    <div class="w-full h-full flex flex-col text-center items-center justify-center gap-4" v-if="demoStatus !== 'ready' && demoStatus !== 'failed'">
      <svg class="animate-spin h-16 w-16 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <h1 class="text-text-primary text-bold text-xl">{{ $t('viewer.page.processing') }}</h1>
      <h2 class="text-text-secondary w-72">
        {{ $t('viewer.page.processing_hint') }}
      </h2>
    </div>

    <!-- Failed State -->
    <div class="w-full h-full flex flex-col text-center items-center justify-center gap-4" v-else-if="demoStatus === 'failed'">
      <div class="w-16 h-16 rounded-full bg-danger-900 flex justify-center items-center">
        <img class="h-10" src="../assets/icons/error.svg">
      </div>
      <h1 class="text-text-primary text-bold text-xl">{{ $t('viewer.page.failed') }}</h1>
      <i18n-t scope="global" keypath="viewer.page.failed_hint" tag="h2" class="text-text-secondary w-72">
        <template #discord><a href="https://discord.gg/Refrag" target="_blank" class="cursor-pointer underline text-text-primary">discord</a></template>
        <template #id><span class="text-danger-500">{{ route.params.uuid ?? route.params.id }}</span></template>
      </i18n-t>
      <router-link to="/demos" class="px-4 py-3 bg-gray-800 rounded-md flex gap-2 items-center text-text-primary">
        <img class="h-4 w-4 brightness-200" src="../assets/icons/leftarrow.svg" />
        {{ $t('viewer.page.go_back') }}
      </router-link>
    </div>

    <!-- Main Viewer -->
    <div v-else class="h-full w-full flex flex-col min-h-0 overflow-hidden">
      <div class="w-full flex-1 flex min-h-0 overflow-hidden">
        <!-- SVG Canvas Area -->
        <div class="w-full flex-1 relative min-h-0 overflow-hidden" ref="canvasContainer">
          <ViewerControls
            :drag-hand-active="dragHandActive"
            :pen-tool-active="penToolActive"
            :drawn-paths-length="drawnPaths.length"
            :show-round-info="showRoundInfo"
            :show-grenade-groups="showGrenadeGroups"
            :capture-disabled="captureDisabled"
            :can-copy="canCopy"
            :show-mode-switch="show3dSwitch"
            :viewer-mode="viewerMode"
            @update:viewer-mode="setViewerMode"
            @screenshot="(method) => takeScreenshot({ method })"
            @export-gif="gifDialogOpen = true"
            @go-back="goBack"
            @toggle-drag-hand="toggleDragHand"
            @toggle-pen-tool="togglePenTool"
            @clear-drawings="clearDrawings"
            @update:show-round-info="showRoundInfo = $event"
            @update:show-grenade-groups="showGrenadeGroups = $event"
          />

          <ViewerGifDialog
            v-if="gifDialogOpen"
            :exporting="gifExporting"
            :progress="gifProgress"
            :range-text="gifRangeText"
            @export="exportGif"
            @cancel="cancelGif"
          />

          <ViewerHud
            v-if="roundData"
            :round-data="roundData"
            :current-round="currentRound"
            :current-tick="currentTick"
            :bomb-plant-event="bombPlantEvent"
            :round-ended="roundEnded"
            :active-kills="activeKills"
            :get-time-of-tick="getTimeOfTick"
            :is-loading-round="isLoadingRound"
          />

          <!-- Loading Spinner Overlay -->
          <div
            v-if="isLoadingRound || roundLoadFailed"
            class="absolute inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-40"
          >
            <div v-if="roundLoadFailed" class="flex flex-col items-center gap-3">
              <p class="text-text-secondary text-sm">{{ $t('viewer.page.round_load_failed', { n: currentRound }) }}</p>
              <button class="px-4 py-3 bg-gray-800 rounded-md text-text-primary" @click="loadRoundData()">{{ $t('viewer.page.try_again') }}</button>
            </div>
            <div v-else class="flex flex-col items-center gap-3">
              <svg class="animate-spin h-16 w-16 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <!-- Only shown once the round is known to be building, so a warm
                   round never flashes a message about work that is not happening. -->
              <p v-if="isPreparingRound" class="text-text-secondary text-sm">{{ $t('viewer.page.preparing_round') }}</p>
            </div>
          </div>

          <Viewer3dCanvas
            v-if="viewerMode === '3d'"
            :round-data="roundData"
            :map-info="mapInfo"
            :current-frame="currentFrame"
            :interpolation-progress="interpolationProgress"
            :play-demo="playDemo"
            :camera="camera3d"
            :selected-steam-id="selectedSteamID"
            :weapon-states="weaponStates"
            :grenade-groups="grenadeGroups"
            :selected-grenade-group-id="selectedGrenadeGroupId"
            :show-grenade-info="showGrenadeGroups"
            @update:camera="setCamera"
            @select="selectedSteamID = $event"
            @select-grenade="selectGrenade3d"
            @opened="(mapSource) => track3d('viewer_3d_opened', { map_source: mapSource })"
            @failed="(failure) => track3d('viewer_3d_failed', failure)"
            @switched="(change) => track3d('viewer_map_source_switched', change)"
            @back="setViewerMode('2d')"
          />
          <ViewerCanvas
            v-else
            ref="viewerCanvas"
            :svg-width="svgWidth"
            :svg-height="svgHeight"
            :map-image-src="mapImageSrc"
            :map-config="mapConfig"
            :pan-x="panX"
            :pan-y="panY"
            :zoom-level="zoomLevel"
            :pen-tool-active="penToolActive"
            :drag-hand-active="dragHandActive"
            :drawn-paths="drawnPaths"
            :current-path="currentPath"
            :pen-color="penColor"
            :pen-stroke-width="penStrokeWidth"
            :sorted-players="sortedPlayers"
            :sorted-nades-by-z="sortedNadesByZ"
            :rendered-bomb="renderedBomb"
            :rendered-fires="renderedFires"
            :rendered-smokes="renderedSmokes"
            :rendered-damages="renderedDamages"
            :rendered-weapon-fires="renderedWeaponFires"
            :rendered-explosions="renderedExplosions"
            :player-radius="playerRadius"
            :blinded-ring-extra="blindedRingExtra"
            :blinded-ring-stroke-width="blindedRingStrokeWidth"
            :bomb-scale="bombScale"
            :bomb-path-data="bombPathData"
            :name-font-size="nameFontSize"
            :name-bg-height="nameBgHeight"
            :name-bg-y-offset="nameBgYOffset"
            :name-text-y-offset="nameTextYOffset"
            :name-bg-rx="nameBgRx"
            :weapon-icon-size="weaponIconSize"
            :weapon-icon-offset-x="weaponIconOffsetX"
            :weapon-icon-offset-y="weaponIconOffsetY"
            :nade-trail-stroke-width="nadeTrailStrokeWidth"
            :nade-trail-dash-array="nadeTrailDashArray"
            :nade-bg-radius="nadeBgRadius"
            :nade-icon-size="nadeIconSize"
            :nade-fallback-radius="nadeFallbackRadius"
            :nade-fallback-stroke-width="nadeFallbackStrokeWidth"
            :fire-stroke-width="fireStrokeWidth"
            :fire-text-font-size="fireTextFontSize"
            :smoke-stroke-width="smokeStrokeWidth"
            :smoke-text-font-size="smokeTextFontSize"
            :bomb-defuse-width="bombDefuseWidth"
            :bomb-defuse-height="bombDefuseHeight"
            :bomb-defuse-offset-x="bombDefuseOffsetX"
            :bomb-defuse-offset-y="bombDefuseOffsetY"
            :bomb-defuse-rx="bombDefuseRx"
            :get-triangle-points="getTrianglePoints"
            :get-triangle-transform="getTriangleTransform"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @wheel="handleWheel"
            @drawing-mousedown="handleDrawingMouseDown"
            @drawing-mousemove="handleDrawingMouseMove"
            @drawing-mouseup="handleDrawingMouseUp"
            @toggle-maps="toggleMaps"
          />
          <ViewerInputOverlay
            v-if="viewerMode === '3d' && roundData?.frames?.length"
            :round-data="roundData"
            :current-frame="currentFrame"
            :camera="camera3d"
            :selected-steam-id="selectedSteamID"
            :weapon-states="weaponStates"
          />
        </div>

        <ViewerSidebar
          :round-data="roundData"
          :ct-players="ctPlayers"
          :t-players="tPlayers"
          :sorted-c-t-players="sortedCTPlayers"
          :sorted-t-players="sortedTPlayers"
          :get-primary="getPrimary"
          :get-sorted-inventory="getSortedInventory"
          @select="selectedSteamID = $event"
        />
      </div>

      <ViewerTimeline
        :round-data="roundData"
        :play-demo="playDemo"
        :frame-rate="frameRate"
        v-model:slider-frame="sliderFrame"
        :show-grenade-groups="showGrenadeGroups"
        :show-round-info="showRoundInfo"
        :grenade-groups="grenadeGroups"
        :timeline-kills="timelineKills"
        :timeline-events="timelineEvents"
        :selected-grenade-group-id="selectedGrenadeGroupId"
        :current-round="currentRound"
        :total-rounds="totalRounds"
        :get-tick-time="getTickTime"
        :get-time-of-tick="getTimeOfTick"
        :get-grenade-thrower-name="getGrenadeThrowerName"
        :get-round-end-reason-icon="getRoundEndReasonIcon"
        :round-winner="roundWinner"
        :is-loading-round="isLoadingRound"
        @toggle-play="handleTogglePlay"
        @step-frame="stepFrame"
        @update-frame-rate="updateFrameRate"
        @slider-mouse-down="onSliderMouseDown"
        @slider-mouse-up="onSliderMouseUp"
        @select-grenade-group="selectGrenadeGroup"
        @copy-succeeded="copySucceeded"
        @launch-grenade="launchGrenade"
        @select-round="selectRound"
      />
    </div>
  </Dashboard>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount, nextTick, defineAsyncComponent,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import axios from 'axios';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import { useDemoLoader } from '../composables/useDemoLoader.js';
import { usePlayback } from '../composables/usePlayback.js';
import { useRenderer } from '../composables/useRenderer.js';
import { useInputTools } from '../composables/useInputTools.js';
import { useViewerCapture } from '../composables/useViewerCapture';
import ViewerHud from '../components/DemoViewer/ViewerHud.vue';
import ViewerSidebar from '../components/DemoViewer/ViewerSidebar.vue';
import ViewerTimeline from '../components/DemoViewer/ViewerTimeline.vue';
import ViewerCanvas from '../components/DemoViewer/ViewerCanvas.vue';
import ViewerControls from '../components/DemoViewer/ViewerControls.vue';
import ViewerGifDialog from '../components/DemoViewer/ViewerGifDialog.vue';
import ViewerInputOverlay from '../components/DemoViewer/ViewerInputOverlay.vue';
import { findBombPlantEvent } from '../utils/demoViewer/bombRenderer.js';
import { createViewerSession, entryFrom } from '../utils/demoViewer/viewerAnalytics.js';
import { CAMERAS } from '../utils/demoViewer/cameras.js';
import { cyclePlayer } from '../utils/demoViewer/selection.js';
import { buildWeaponStates } from '../utils/demoViewer/weaponStates.js';
import { canUse3dViewer, hasWebgl2, VIEWER_3D_FEATURE } from '../utils/viewer3d.js';
import { trackEvent } from '../utils/analytics';

// Async so 2D users never download three.js.
const Viewer3dCanvas = defineAsyncComponent(() => import('../components/DemoViewer/Viewer3dCanvas.vue'));

const route = useRoute();
const router = useRouter();
const store = useStore();
const auth = useAuth();
const { t } = useI18n();

// wi_6b5960f9-952c-48fc-ab25-68e9f9e5b2da: the 2D/3D switch, for allowlisted users on desktop only.
const VIEWER_MODE_KEY = 'refrag_viewer_mode';
const may3d = canUse3dViewer(auth?.user());
const webgl2Missing = may3d && !hasWebgl2();
const show3dSwitch = may3d && !webgl2Missing && window.innerWidth >= 1024;
const viewerMode = ref(show3dSwitch && localStorage.getItem(VIEWER_MODE_KEY) === '3d' ? '3d' : '2d');
// Fetch the 3D chunk early for those who can open it, so the switch does not wait on it.
if (show3dSwitch) import('../components/DemoViewer/Viewer3dCanvas.vue').catch(() => {});

// Usage events. The fixture demos 'test' and '4' send nothing.
const viewerSession = route.params.id === 'test' || route.params.id === '4' ? null : createViewerSession({
  demoId: route.params.uuid ?? route.params.id,
  entry: entryFrom(window.history.state?.back, document.referrer, window.location.origin),
});

const canvasContainer = ref(null);
const viewerCanvas = ref(null);
const svgWidth = ref(1920);
const svgHeight = ref(1080);
const resizeObserver = ref(null);

const showGrenadeGroups = ref(true);
const showRoundInfo = ref(true);

function goBack() {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/demos');
  }
}

const mapConfig = computed(() => {
  if (!canvasContainer.value) {
    return {
      x: 0, y: 0, width: 0, height: 0,
    };
  }
  const sideLength = canvasContainer.value.offsetWidth * 0.6;
  return {
    x: (svgWidth.value / 2) - (sideLength / 2),
    y: (svgHeight.value / 2) - (sideLength / 2),
    width: sideLength,
    height: sideLength,
  };
});

const {
  demoStatus,
  roundData,
  mapInfo,
  currentRound,
  totalRounds,
  roundInfo,
  mapImageSrc,
  showLowerMap,
  isLoadingRound,
  isPreparingRound,
  roundLoadFailed,
  subscribeToDemo,
  loadDemoData,
  loadRoundData,
  toggleMaps,
  cleanup: cleanupDemo,
} = useDemoLoader(route, store);

const {
  playerCache,
  allPlayersCache,
  frameTickMap,
  bombEventCache,
  bombPlantEvent,
  ctPlayers,
  tPlayers,
  playerStates,
  renderedPlayers,
  renderedNades,
  renderedBomb,
  renderedFires,
  renderedSmokes,
  renderedDamages,
  renderedWeaponFires,
  renderedExplosions,
  activeKills,
  selectedGrenadeGroupId,
  pixelsPerMapUnit,
  playerRadius,
  blindedRingExtra,
  blindedRingStrokeWidth,
  bombScale,
  bombPathData,
  nameFontSize,
  nameBgHeight,
  nameBgYOffset,
  nameTextYOffset,
  nameBgRx,
  weaponIconSize,
  weaponIconOffsetX,
  weaponIconOffsetY,
  nadeTrailStrokeWidth,
  nadeTrailDashArray,
  nadeBgRadius,
  nadeIconSize,
  nadeFallbackRadius,
  nadeFallbackStrokeWidth,
  fireStrokeWidth,
  fireTextFontSize,
  smokeStrokeWidth,
  smokeTextFontSize,
  bombDefuseWidth,
  bombDefuseHeight,
  bombDefuseOffsetX,
  bombDefuseOffsetY,
  bombDefuseRx,
  getConvertedCords,
  getTrianglePoints,
  getTriangleTransform,
  clearPlayerStates,
  clearCanvas,
  initializeCaches,
  updatePlayers,
  pushPlayer,
  getPrimary,
  getSortedInventory,
  getGrenadeGroups,
  getGrenadeThrowerName,
  timelineEvents,
  grenadeGroups,
  timelineKills,
  getTimelineEvents,
  getRoundEndReasonIcon,
  roundWinner,
  getTimeOfTick,
  getTickTime,
  updateKills,
  renderAt,
  sortedPlayers,
  sortedCTPlayers,
  sortedTPlayers,
  sortedNadesByZ,
  setRefs,
} = useRenderer(roundData, mapInfo, canvasContainer, svgWidth, svgHeight, mapConfig, currentRound, roundInfo);

const {
  currentFrame,
  currentTick,
  previousTick,
  sliderFrame,
  playDemo,
  frameRate,
  interpolationProgress,
  roundEnded,
  togglePlay,
  stopPlay,
  updateFrameRate,
  increaseFrameRate,
  decreaseFrameRate,
  handleSliderMouseDown,
  handleSliderMouseUp,
  handleKeyPress: playbackHandleKeyPress,
  setFrameAdvanceCallback,
} = usePlayback(
  roundData,
  () => {
    // When scrubbing, ensure currentTick is updated from currentFrame before updating players
    if (!playDemo.value && roundData.value && roundData.value.frames && roundData.value.frames[currentFrame.value]) {
      const frame = roundData.value.frames[currentFrame.value];
      if (frame && frame.tick !== currentTick.value) {
        previousTick.value = currentTick.value;
        currentTick.value = frame.tick;
      }
    }
    updatePlayers();
    renderAt(
      currentFrame.value,
      currentTick.value,
      previousTick.value,
      playDemo.value,
      frameRate.value,
      svgWidth.value,
    );
    updateKills(currentTick.value, previousTick.value, currentTick.value);
  },
  null,
);

setRefs(currentFrame, currentTick, playDemo, interpolationProgress);

function loadTick() {
  if (!roundData.value || !roundData.value.frames || currentFrame.value >= roundData.value.frames.length) {
    if (playDemo.value) {
      stopPlay();
    }
    return;
  }

  const frame = roundData.value.frames[currentFrame.value];
  if (!frame) return;

  previousTick.value = currentTick.value;
  currentTick.value = frame.tick;

  bombPlantEvent.value = findBombPlantEvent(roundData.value, currentTick.value);

  if (!playDemo.value) {
    updatePlayers();
  }

  renderAt(
    currentFrame.value,
    currentTick.value,
    previousTick.value,
    playDemo.value,
    frameRate.value,
    svgWidth.value,
  );

  updateKills(currentTick.value, previousTick.value, currentTick.value);
}

function handleFrameAdvance() {
  loadTick();
}

setFrameAdvanceCallback(handleFrameAdvance);

const {
  zoomLevel,
  panX,
  panY,
  isDragging,
  penToolActive,
  dragHandActive,
  drawingOverlay,
  isDrawing,
  drawnPaths,
  currentPath,
  currentPathPoints,
  penColor,
  penStrokeWidth,
  getSVGCoordinates,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleWheel,
  resetZoom,
  toggleDragHand,
  togglePenTool,
  clearDrawings,
  handleDrawingMouseDown,
  handleDrawingMouseMove,
  handleDrawingMouseUp,
  finishCurrentPath,
} = useInputTools(viewerCanvas);

const gifDialogOpen = ref(false);

// The GIF export moves the viewer through its frames with this.
function seekFrame(index) {
  currentFrame.value = index;
  sliderFrame.value = index;
  loadTick();
}

async function exportGif(options) {
  await exportGifFrames(options);
  gifDialogOpen.value = false;
}

function cancelGif() {
  if (gifExporting.value) cancelGifExport();
  else gifDialogOpen.value = false;
}

const {
  canCopy,
  captureDisabled,
  takeScreenshot,
  gifExporting,
  gifProgress,
  gifRangeText,
  exportGif: exportGifFrames,
  cancelGif: cancelGifExport,
} = useViewerCapture({
  route,
  store,
  viewerCanvas,
  canvasContainer,
  svgWidth,
  svgHeight,
  mapInfo,
  currentRound,
  currentTick,
  bombPlantEvent,
  roundEnded,
  activeKills,
  getTimeOfTick,
  // The capture reads the 2D SVG, which is not mounted in 3D.
  isUnavailable: () => isLoadingRound.value || roundLoadFailed.value || !roundData.value || viewerMode.value === '3d',
  roundData,
  currentFrame,
  isPlaying: () => playDemo.value,
  stopPlay,
  resumePlay: togglePlay,
  seekFrame,
});

function track3d(name, properties) {
  trackEvent(name, {
    factory_feature: VIEWER_3D_FEATURE,
    demo_id: route.params.uuid ?? route.params.id,
    map: mapInfo.value?.name ?? null,
    round: currentRound.value,
    ...properties,
  });
}

// Only the canvas changes: round, frame and play state stay as they are.
function setViewerMode(mode) {
  if (mode === viewerMode.value) return;
  track3d('viewer_mode_switched', { from: viewerMode.value, to: mode });
  viewerMode.value = mode;
  localStorage.setItem(VIEWER_MODE_KEY, mode);
}

// 3D camera and the player it follows. The selection is kept across rounds by steamID.
const camera3d = ref('free');
const selectedSteamID = ref(null);
// Zoom and reload per weapon id, folded once per round for the POV FOV, scope and overlay.
const weaponStates = computed(() => buildWeaponStates(roundData.value?.frames));

function setCamera(camera) {
  if (camera === camera3d.value) return;
  camera3d.value = camera;
  track3d('viewer_camera_changed', { camera });
}

function handle3dKey(e) {
  if (e.code === 'KeyC' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    setCamera(CAMERAS[(CAMERAS.indexOf(camera3d.value) + 1) % CAMERAS.length]);
    return true;
  }
  if (e.code === 'BracketLeft' || e.code === 'BracketRight') {
    const frame = roundData.value?.frames?.[currentFrame.value];
    if (frame) selectedSteamID.value = cyclePlayer(frame, selectedSteamID.value, e.code === 'BracketRight' ? 1 : -1);
    return true;
  }
  return false;
}

watch(currentTick, (newVal, oldVal) => {
  if (roundData.value && currentTick.value > roundData.value.endTick) {
    roundEnded.value = true;
  } else {
    roundEnded.value = false;
  }
});

watch(roundData, (newVal) => {
  if (newVal && newVal.frames && newVal.frames.length > 0) {
    initializeCaches();
    nextTick(() => {
      updateSVGDimensions();
      loadTick();
    });
  }
}, { immediate: true });

if (viewerSession) {
  viewerSession.visible(document.visibilityState === 'visible');
  watch(roundData, (data) => {
    if (data?.frames?.length > 0) {
      viewerSession.opened({ map: mapInfo.value?.name ?? null, roundNumber: currentRound.value, totalRounds: totalRounds.value });
    }
  }, { immediate: true });
  watch(playDemo, (value) => viewerSession.playing(value));
  watch(currentRound, (value) => viewerSession.roundShown(value));
  watch(frameRate, (value) => viewerSession.frameRateChanged(value));
}

function onVisibilityChange() {
  viewerSession?.visible(document.visibilityState === 'visible');
}

function onPageHide() {
  viewerSession?.end();
}

let sliderStartFrame = null;

function onSliderMouseDown() {
  sliderStartFrame = currentFrame.value;
  handleSliderMouseDown();
}

function onSliderMouseUp() {
  handleSliderMouseUp();
  if (sliderStartFrame !== null) {
    viewerSession?.seeked({ method: 'slider', from: sliderStartFrame, to: currentFrame.value });
  }
  sliderStartFrame = null;
}

function selectRound(round) {
  stopPlay();
  clearCanvas();
  showLowerMap.value = false;
  history.replaceState({}, window.location, `?round=${round}`);
  currentRound.value = round;
  currentFrame.value = 0;
  sliderFrame.value = 0;
  currentTick.value = 0;
  previousTick.value = 0;
  loadRoundData();
}

// One exact frame back (-1) or forward (+1), in both modes. Pauses playback first.
function stepFrame(delta) {
  if (!roundData.value?.frames?.length) return;
  if (playDemo.value) stopPlay();
  currentFrame.value = Math.max(0, Math.min(currentFrame.value + delta, roundData.value.frames.length - 1));
  sliderFrame.value = currentFrame.value;
  interpolationProgress.value = 0;
  loadTick();
}

function handleTogglePlay() {
  if (isLoadingRound.value) {
    return;
  }
  togglePlay();
}

function selectGrenadeGroup(group, mode = '2d', grenade = group[0]) {
  if (selectedGrenadeGroupId.value === group[0].uniqueID) {
    selectedGrenadeGroupId.value = null;
    return;
  }
  selectedGrenadeGroupId.value = group[0].uniqueID;
  track3d('viewer_grenade_selected', { viewer_mode: mode, grenade_type: grenade.grenadeType });
}

// A grenade clicked in 3D selects the timeline group it belongs to.
function selectGrenade3d(grenade) {
  const group = grenadeGroups.value.find((g) => g.some((n) => String(n.uniqueID) === String(grenade.uniqueID)));
  if (group) selectGrenadeGroup(group, '3d', grenade);
}

function launchGrenade(grenade) {
  axios({
    url: `/cs_servers/${store.getters.activeServer.id}/exec_server_command`,
    method: 'POST',
    data: {
      command: 'teleport_to_nade',
      value: `${grenade.throwerX} ${grenade.throwerY} ${grenade.throwerZ}, ${grenade.throwerViewY} ${grenade.throwerViewX}, ${parseFloat(grenade.grenadePower) || 1}, ${grenade.isDucked || 0}, ${grenade.grenadeSimMode}`,
      is_client_command: true,
    },
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  });
}

function copySucceeded() {
  store.commit('showMessage', {
    messageType: 'success',
    messageText: t('viewer.page.position_copied'),
  });
  setTimeout(() => {
    store.commit('hideMessage');
  }, 3000);
}

function updateSVGDimensions() {
  if (canvasContainer.value) {
    const oldWidth = svgWidth.value;
    const oldHeight = svgHeight.value;

    svgWidth.value = canvasContainer.value.offsetWidth;
    svgHeight.value = canvasContainer.value.offsetHeight;

    if (roundData.value && (Math.abs(oldWidth - svgWidth.value) > 1 || Math.abs(oldHeight - svgHeight.value) > 1)) {
      nextTick(() => {
        loadTick();
      });
    }
  }
}

function handleKeyPress(e) {
  if (gifExporting.value) return;
  if (viewerMode.value === '3d' && handle3dKey(e)) return;
  if (e.code === 'KeyC' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    takeScreenshot({ method: 'shortcut' });
    return;
  }
  if (e.code === 'Comma' || e.code === 'Period') {
    stepFrame(e.code === 'Comma' ? -1 : 1);
    return;
  }
  const fromFrame = currentFrame.value;
  playbackHandleKeyPress(e, {
    onNextRound: () => {
      if (currentRound.value < totalRounds.value) {
        selectRound(currentRound.value + 1);
      }
    },
    onPrevRound: () => {
      if (currentRound.value > 1) {
        selectRound(currentRound.value - 1);
      }
    },
    onToggleMap: toggleMaps,
  });
  if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
    viewerSession?.seeked({ method: 'keyboard', from: fromFrame, to: currentFrame.value });
  }
}

useHead({
  title: () => t('viewer.page.head_title'),
});

onBeforeMount(() => {
  // A match-keyed viewer has no Demo to push status; the loader's recheck covers it.
  if (route.params.id && route.params.id !== 'test' && route.params.id !== '4') {
    subscribeToDemo();
  }
  loadDemoData();
});

onMounted(() => {
  updateSVGDimensions();

  if (canvasContainer.value) {
    resizeObserver.value = new ResizeObserver(() => {
      updateSVGDimensions();
    });
    resizeObserver.value.observe(canvasContainer.value);
  }

  window.addEventListener('resize', updateSVGDimensions);
  window.addEventListener('keydown', handleKeyPress);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  if (webgl2Missing) track3d('viewer_3d_failed', { reason: 'webgl_unavailable', detail: null });
});

onBeforeUnmount(() => {
  viewerSession?.end();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pagehide', onPageHide);
  stopPlay();

  if (resizeObserver.value && canvasContainer.value) {
    resizeObserver.value.unobserve(canvasContainer.value);
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }

  window.removeEventListener('resize', updateSVGDimensions);
  window.removeEventListener('keydown', handleKeyPress);

  cleanupDemo();
  clearCanvas();
  clearPlayerStates();

  playerCache.value.clear();
  allPlayersCache.value.clear();
  frameTickMap.value.clear();

  ctPlayers.value = [];
  tPlayers.value = [];

  roundData.value = null;
  roundInfo.value = null;
  mapInfo.value = null;
  playerStates.value = {};
  bombPlantEvent.value = null;
});
</script>
